import { editor, Uri, IDisposable, KeyCode, KeyMod, Position, IPosition } from 'monaco-editor-core';
import { BaseCodeEditorOptions, CodeEditorValue, LanguageService, LuceneCodeEditorOptions } from './interface';
import { EventEmitter } from 'events';
import { LUCENE_ID } from './languages/lucene/constants';
import { LuceneLanguageService } from './languages/lucene/lucene.language.service';
import { v4 as uuidv4 } from 'uuid';
import { SQLITE_ID } from './languages/sqlite/constants';
import { SqliteLanguageService } from './languages/sqlite/sqlite.language.service';
import { YARA_ID } from './languages/yara/constants';
import { YaraLanguageService } from './languages/yara/yara.language.service';

export class CodeEditor extends EventEmitter {
  public static KeyCode = KeyCode;
  public static KeyMod = KeyMod;

  private isDisposed = false;
  private disposable: IDisposable[] = [];
  private base: editor.IStandaloneCodeEditor;
  private model: editor.ITextModel;
  private languageService: LanguageService;
  private decorations: string[] = [];

  static create(domElement: HTMLElement, options: LuceneCodeEditorOptions): CodeEditor;
  static create(domElement: HTMLElement, options: BaseCodeEditorOptions): CodeEditor {
    return new CodeEditor(domElement, options);
  }

  public static defineTheme(themeName: string, themeData: editor.IStandaloneThemeData) {
    editor.defineTheme(themeName, themeData);
  }

  public static setTheme(themeName: string): void {
    editor.setTheme(themeName);
  }

  public static setModelLanguage(model: editor.ITextModel, languageId: string): void {
    editor.setModelLanguage(model, languageId);
  }

  private constructor(domElement: HTMLElement, options: BaseCodeEditorOptions) {
    super();
    // TODO: validate options

    const { language, languageOptions, editorOptions } = options;
    switch (language) {
      case LUCENE_ID:
        this.languageService = LuceneLanguageService.getInstance();
        break;
      case SQLITE_ID:
        this.languageService = SqliteLanguageService.getInstance();
        break;
      case YARA_ID:
        this.languageService = YaraLanguageService.getInstance();
        break;
      default:
        throw new Error(`Unknown language ${language}`);
    }

    this.model = editor.createModel(editorOptions.value, language, Uri.parse(`editor://${language}-${uuidv4()}`));
    this.disposable.push(this.model);
    try {
      this.languageService.registerModelOptions(this.model, languageOptions);
      this.base = editor.create(domElement, {
        ...editorOptions,
        model: this.model,
      });
      this.disposable.push(this.base);
    } catch (error) {
      this.dispose();
      throw error;
    }

    this.setValidation(this.model);
  }

  private setValidation(model: editor.ITextModel) {
    let timeout: number;
    let inProgress = false;
    let validityState = true;

    const validate = async () => {
      if (inProgress) {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(validate, 500);
        return;
      }

      inProgress = true;
      // TODO: add timeout option to prevent infinite wait
      const isValid = await this.languageService.doValidate(model);
      if (isValid !== validityState) {
        validityState = isValid;
        this.emit('validityChange', isValid);
      }

      inProgress = false;
    };

    this.disposable.push(
      model.onDidChangeContent(() => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(validate, 500);
      })
    );

    setTimeout(validate, 0);
  }

  public getText() {
    return this.base.getValue();
  }

  public async getValue(): Promise<CodeEditorValue> {
    const raw = this.base.getValue();
    const isValid = await this.languageService.doValidate(this.model);
    try {
      const parsed = this.languageService.parse(raw);
      return { raw, parsed, isValid };
    } catch {
      return { raw, parsed: null, isValid };
    }
  }

  public setValue(value: string) {
    this.base.setValue(value);
  }

  public dispose() {
    if (this.isDisposed) {
      return;
    }

    this.isDisposed = true;
    for (const item of this.disposable) {
      item.dispose();
    }
  }

  public addCommand(keybinding: number, handler: editor.ICommandHandler, context?: string): string | null {
    return this.base.addCommand(keybinding, handler, context);
  }

  public onDidBlurEditorWidget(listener: () => void) {
    this.base.onDidBlurEditorWidget(listener);
  }

  public trigger(source: string | null | undefined, handlerId: string, payload: any) {
    this.base.trigger(source, handlerId, payload);
  }

  public onDidFocusEditorWidget(listener: () => void) {
    this.base.onDidFocusEditorWidget(listener);
  }

  public onDidFocusEditorText(listener: () => void) {
    this.base.onDidFocusEditorText(listener);
  }

  public onDidBlurEditorText(listener: () => void) {
    this.base.onDidBlurEditorText(listener);
  }

  public onDidLayoutChange(listener: () => void) {
    this.base.onDidLayoutChange(listener);
  }

  public onDidContentSizeChange(listener: () => void) {
    this.base.onDidContentSizeChange(listener);
  }

  public onDidPaste(listener: () => void) {
    this.base.onDidPaste(listener);
  }

  public onDidChangeModelContent(listener: () => void) {
    this.base.onDidChangeModelContent(listener);
  }

  public addAction(descriptor: editor.IActionDescriptor): IDisposable {
    return this.base.addAction(descriptor);
  }

  public focus(): void {
    return this.base.focus();
  }

  public getPosition(): Position | null {
    return this.base.getPosition();
  }

  public setPosition(position: IPosition): void {
    this.base.setPosition(position);
  }

  public getModel(): editor.ITextModel | null {
    return this.base.getModel();
  }

  public getViewModel(): editor.ITextModel {
    //Get the line count (including the auto wrapped lines)
    const base = this.base as any;
    return base._getViewModel();
  }

  public updateOptions(newOptions): void {
    const { languageOptions, editorOptions } = newOptions;
    this.languageService.registerModelOptions(this.model, languageOptions);
    this.base.updateOptions(editorOptions);
  }

  public getContentHeight(): number {
    return this.base.getContentHeight();
  }

  public revealLine(lineNumber: number): void {
    this.base.revealLine(lineNumber);
  }

  public setModelMarkers(markers) {
    editor.setModelMarkers(this.model, this.model.getLanguageId(), markers);
  }

  public setDecorations(decorations: editor.IModelDeltaDecoration[]) {
    this.decorations = this.base.deltaDecorations(this.decorations, decorations);
  }

  public getLineFirstNonWhitespaceColumn(lineNumber): number {
    return this.base.getModel()?.getLineFirstNonWhitespaceColumn(lineNumber) || 0;
  }

  public getLineLastNonWhitespaceColumn(lineNumber): number {
    return this.base.getModel()?.getLineLastNonWhitespaceColumn(lineNumber) || 0;
  }
}
